import { styled } from "@mui/material";
import type { CSSObject } from "@mui/material/styles";
import { DataGridPro, DataGridProProps } from "@mui/x-data-grid-pro";

/**
 * Prefix of the class name marking a row with its position in the tree, `<prefix><depth>`.
 * Rows get the class from {@link getRowLevelClassName}.
 */
const ROW_LEVEL_CLASS_PREFIX = "TableLikeDataGrid-rowLevel-";

/**
 * Marks the cells of the rate columns (price, wage, margin, margin percentage), so they can be styled
 * apart from the grouping cell holding the label of the row.
 */
export const RATE_CELL_CLASS = "TableLikeDataGrid-rateCell";

/** Number of hierarchy levels the tree can render, so the deepest level is `ROW_LEVEL_COUNT - 1`. */
const ROW_LEVEL_COUNT = 5;

/**
 * Class name of a row according to its depth in the tree. Anything deeper than the levels the table can
 * render falls back to the class of the deepest level.
 *
 * @param depth - Depth of the row node, `0` for the top level of the tree
 */
export const getRowLevelClassName = (depth: number): string =>
  `${ROW_LEVEL_CLASS_PREFIX}${Math.min(Math.max(depth, 0), ROW_LEVEL_COUNT - 1)}`;

/**
 * Drops the bottom border of a row whose next row sits on the same level, so a run of rows sharing one
 * level reads as a single block and only the boundary between two levels is drawn. Rows are always direct
 * siblings inside the render zone of the grid and the detail panels live in a container of their own,
 * which makes the sibling combinator see the row that follows visually. The last row of the table has no
 * next row and keeps its border.
 */
const buildLevelBoundaryStyles = (): CSSObject =>
  Object.fromEntries(
    Array.from({ length: ROW_LEVEL_COUNT }, (_, depth): [string, CSSObject] => [
      `& .${ROW_LEVEL_CLASS_PREFIX}${depth}:has(+ .${ROW_LEVEL_CLASS_PREFIX}${depth}) .MuiDataGrid-cell`,
      { borderBottom: "none" },
    ])
  );

export const CalculationDataGrid = styled(DataGridPro)<DataGridProProps>(({ theme }) => ({
  "& .MuiDataGrid-row--detailPanelExpanded": {
    backgroundColor: theme.palette.custom.deepSpace["200"],
  },
  "& .MuiDataGrid-columnHeaders .MuiDataGrid-filler": {
    backgroundColor: theme.palette.grey["200"],
  },
  "& .TableLikeDataGrid-header-filter": {
    "& .MuiDataGrid-columnHeaderTitleContainerContent": { width: "100%", height: "100%" },
    width: "100%",
  },
  "& .TableLikeDataGrid-columnHeader": {
    backgroundColor: theme.palette.grey["200"],
    "& .TableLikeDataGrid-columnHeaderTitle": {
      fontWeight: theme.typography.fontWeightBold,
      color: theme.palette.text.primary,
    },
  },
  "& .TableLikeDataGrid-totalRow": {
    backgroundColor: "#FFF5E8",
  },
  "& .TableLikeDataGrid-row.TableLikeDataGrid-foldableRow": {
    cursor: "pointer",
  },
  "& .MuiDataGrid-cell": {
    borderBottom: "1px solid var(--DataGrid-rowBorderColor)",
    borderTop: "none",
  },
  "& .TableLikeDataGrid-grouping-cell.TableLikeDataGrid": {
    paddingLeft: 0,
    "&.TableLikeDataGrid-depth-1": {
      marginLeft: theme.spacing(2),
      width: `calc(var(--width, 0px) - ${theme.spacing(2)})`,
    },
    "&.TableLikeDataGrid-depth-2": {
      marginLeft: theme.spacing(4),
      width: `calc(var(--width, 0px) - ${theme.spacing(4)})`,
    },
    "&.TableLikeDataGrid-depth-3": {
      marginLeft: theme.spacing(6),
      width: `calc(var(--width, 0px) - ${theme.spacing(6)})`,
    },
    "&.TableLikeDataGrid-depth-4": {
      marginLeft: theme.spacing(8),
      width: `calc(var(--width, 0px) - ${theme.spacing(8)})`,
    },
    "&.TableLikeDataGrid-depth-5": {
      marginLeft: theme.spacing(5),
      width: `calc(var(--width, 0px) - ${theme.spacing(5)})`,
    },
    "&.TableLikeDataGrid-depth-6": {
      marginLeft: theme.spacing(6),
      width: `calc(var(--width, 0px) - ${theme.spacing(6)})`,
    },
  },

  [`& .${RATE_CELL_CLASS} *`]: {
    fontVariantNumeric: "tabular-nums",
  },
  // One hook per hierarchy level of the tree, level 0 being the top most row. Kept last so the styles
  // written here win over the generic row rules above.
  [`& .${ROW_LEVEL_CLASS_PREFIX}0`]: {
    [`& .${RATE_CELL_CLASS} *`]: {
      fontWeight: "600",
    },
  },
  [`& .${ROW_LEVEL_CLASS_PREFIX}1`]: {
    [`& .${RATE_CELL_CLASS} *`]: {
      fontWeight: "400",
    },
  },
  [`& .${ROW_LEVEL_CLASS_PREFIX}2`]: {
    [`& .${RATE_CELL_CLASS}`]: {
      color: theme.palette.grey["800"],
    },
  },
  [`& .${ROW_LEVEL_CLASS_PREFIX}3`]: {
    [`& .${RATE_CELL_CLASS}`]: {
      color: theme.palette.grey["700"],
    },
  },
  [`& .${ROW_LEVEL_CLASS_PREFIX}4`]: {
    [`& .${RATE_CELL_CLASS}`]: {
      color: theme.palette.grey["700"],
      [`& *`]: {
        fontSize: `${theme.typography.pxToRem(13)} !important`,
      },
    },
  },
  // Only the boundary between two levels is drawn, see `buildLevelBoundaryStyles`.
  ...buildLevelBoundaryStyles(),
  "& .MuiDataGrid-detailPanel": {
    overflow: "visible",
    "& .MuiDataGrid-root": {
      "& .MuiDataGrid-row": {
        "&:hover": {
          backgroundColor: "inherit",
        },
        "&  .MuiDataGrid-cell": {
          backgroundColor: theme.palette.grey["100"],
        },
      },
      " & .MuiDataGrid-detailPanel": {
        marginLeft: `calc(${theme.spacing(2)} * (var(--row-depth, 0) + 1))`,
        width: `calc(100% - ${theme.spacing(2)} * (var(--row-depth, 0) + 1))`,
      },
      "& .MuiDataGrid-row .MuiDataGrid-cell:nth-of-type(2)": {
        marginLeft: `calc(${theme.spacing(2)} * (var(--row-depth, 0) + 1))`,
        width: `calc(var(--width, 0px) - (${theme.spacing(2)} * (var(--row-depth, 0) + 1)))`,
      },
    },
  },
})) as typeof DataGridPro;
